import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConnectivity } from '../contexts/ConnectivityContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Container, Box, TextField, Button, Typography, Paper, 
  Alert, CircularProgress, FormControlLabel, Checkbox,
  Slide, Fade, Divider, Grid, Card, CardContent, 
  List, ListItem, ListItemIcon, ListItemText,
  useTheme, useMediaQuery, Popover
} from '@mui/material';
import { getMinutesUntilMidnight } from '../utils/dateHelpers';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import TwoFactorInput from '../components/TwoFactorInput';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PasswordInput from '../components/PasswordInput';
import { authApi } from '../services/api';
import { getPendingLoginCredentials, setPendingLoginCredentials, hasPendingLoginCredentials, clearPendingLoginCredentials } from '../utils/pendingLoginCredentials';

// Icons for features
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SavingsIcon from '@mui/icons-material/Savings';
import CategoryIcon from '@mui/icons-material/Category';
import ReceiptIcon from '@mui/icons-material/Receipt';

const LandingPage = () => {
  const [formData, setFormData] = useState(() => {
    // Initialize from the in-memory pending-credentials store, to persist
    // across unmounts within this session (see pendingLoginCredentials.js
    // for why this isn't localStorage).
    const savedData = getPendingLoginCredentials();
    if (savedData) {
      return {
        identifier: savedData.identifier || '',
        password: savedData.password || ''
      };
    }
    return {
      identifier: '',
      password: ''
    };
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(
    localStorage.getItem('keepSignedIn') === 'true'
  );
  const [error, setError] = useState('');
  const [googleError, setGoogleError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [requires2FA, setRequires2FA] = useState(() => {
    // Initialize from localStorage to persist across unmounts
    return localStorage.getItem('requires2FA') === 'true';
  });
  const [twoFactorError, setTwoFactorError] = useState('');
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [emailVerificationData, setEmailVerificationData] = useState(null);
  const [googlePopupAnchor, setGooglePopupAnchor] = useState(null);
  const [showGooglePopup, setShowGooglePopup] = useState(false);
  
  const { login, googleLogin, updateInactivityTimeout, resendVerificationEmail, checkAuthStatus, currentUser } = useAuth();
  
  // Helper function to calculate minutes until midnight
  const getMinutesUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return Math.ceil((midnight - now) / (1000 * 60));
  };
  const { isOnline, checkServerConnectivity } = useConnectivity();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Check connectivity when component mounts and when there's an error
  useEffect(() => {
    checkServerConnectivity();
  }, [error]); // Removed checkServerConnectivity from dependencies
  
  // Load saved identifier on component mount
  useEffect(() => {
    const savedIdentifier = localStorage.getItem('rememberedIdentifier');
    if (savedIdentifier) {
      setFormData(prev => ({...prev, identifier: savedIdentifier}));
      setRememberMe(true);
    }
  }, []);

  // Listen for changes to keepSignedIn setting from Settings page
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'keepSignedIn') {
        setKeepSignedIn(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Navigate to dashboard when user becomes authenticated
  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);



  // Clear 2FA state when user is already authenticated or when component mounts
  useEffect(() => {
    if (currentUser) {
      localStorage.removeItem('requires2FA');
      clearPendingLoginCredentials();
      setRequires2FA(false);
    }
  }, [currentUser]);

  // Clear 2FA state on component mount if user is not authenticated
  useEffect(() => {
    if (!currentUser && requires2FA) {
      // Check if we should clear the 2FA state (e.g., if user navigated away and came back)
      const shouldClear = !hasPendingLoginCredentials();
      if (shouldClear) {
        localStorage.removeItem('requires2FA');
        setRequires2FA(false);
      }
    }
  }, [currentUser, requires2FA]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };
  
  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };
  
  const handleKeepSignedInChange = (e) => {
    const newValue = e.target.checked;
    setKeepSignedIn(newValue);
    
    // Save to localStorage immediately
    if (newValue) {
      localStorage.setItem('keepSignedIn', 'true');
    } else {
      localStorage.removeItem('keepSignedIn');
    }
    
    // Dispatch storage event to notify other components (like Settings) of the change
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'keepSignedIn',
      newValue: newValue ? 'true' : null,
      oldValue: newValue ? null : 'true'
    }));
  };

  const handle2FASubmit = async (twoFactorCode, trustDevice = false) => {
    console.log('🔍 LandingPage handle2FASubmit called with code:', twoFactorCode);
    try {
      setTwoFactorError('');
      setLoading(true);
      
      // Submit login with 2FA code
      const loginData = {
        identifier: formData.identifier,
        password: formData.password,
        two_fa_code: twoFactorCode
      };
      
      // If "Keep signed in" is checked, add the expiration time
      if (keepSignedIn) {
        const midnight = new Date();
        midnight.setHours(24, 0, 0, 0);
        loginData.expires_at = midnight.getTime();
      }
      
      console.log('🔍 LandingPage submitting 2FA with data:', { ...loginData, password: '***', two_fa_code: '***' });
      
      // Use the auth context login method which will handle the API call
      const loginResponse = await login(loginData.identifier, loginData.password, loginData.expires_at, twoFactorCode);
      
      console.log('🔍 LandingPage 2FA login response:', loginResponse);
      
      // Check if login was successful (has access_token)
      if (loginResponse && loginResponse.access_token) {
        console.log('🔍 LandingPage 2FA login successful, navigating to dashboard');
        // If user wants to trust this device, make the trust device request
        if (trustDevice) {
          try {
            const deviceName = `${navigator.platform} - ${navigator.userAgent.split(' ').pop()}`;
            const trustResponse = await authApi.trustDevice(twoFactorCode, deviceName);
            
            // Store the device token in localStorage
            if (trustResponse.data.device_token) {
              localStorage.setItem('trustedDeviceToken', trustResponse.data.device_token);
            }
          } catch (trustError) {
            // Don't fail the login if device trust fails
          }
        }
        
        // Handle timeout settings
        if (keepSignedIn) {
          const minutesUntilMidnight = getMinutesUntilMidnight();
          updateInactivityTimeout(minutesUntilMidnight);
          localStorage.setItem('keepSignedIn', 'true');
          localStorage.setItem('signInExpiryTime', loginData.expires_at.toString());
        } else {
          updateInactivityTimeout(30);
          localStorage.removeItem('keepSignedIn');
          localStorage.removeItem('signInExpiryTime');
        }
        
        // Clear 2FA state and navigate to dashboard
        localStorage.removeItem('requires2FA');
        clearPendingLoginCredentials();
        navigate('/dashboard');
      } else {
        // If no access_token, it might be an error or unexpected response
        setTwoFactorError('Login failed. Please try again.');
      }
      
    } catch (err) {
      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setTwoFactorError('Network error: Please check your internet connection');
      } else {
        setTwoFactorError(err.response?.data?.error || 'Invalid verification code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTwoFactorError('');
    localStorage.removeItem('requires2FA');
    clearPendingLoginCredentials();
  };

  const handleGoogleSuccess = async (googleToken) => {
    console.log('🔍 LANDING PAGE handleGoogleSuccess CALLED with token:', googleToken?.substring?.(0, 20) + '...');
    try {
      setError('');
      setGoogleError('');
      setLoading(true);

      console.log('🔍 Landing calling googleLogin with token');
      const loginResponse = await googleLogin(googleToken);
      console.log('🔍 Landing received response:', loginResponse);

      // Check if Google login requires 2FA
      if (loginResponse && loginResponse.requires_2fa) {
        console.log('🔍 Landing Google login requires 2FA, redirecting to login page with state');
        // Redirect to login page with 2FA state data
        navigate('/login', {
          state: {
            requiresGoogle2FA: true,
            google2FAData: {
              user_id: loginResponse.user_id,
              is_google_user: loginResponse.is_google_user
            },
            fromGoogleLogin: true
          }
        });
        return;
      }

      // If we get here, login was successful (handled by auth context)
      console.log('🔍 Landing Google login completed successfully');

    } catch (err) {
      console.log('🔍 Landing handleGoogleSuccess ERROR:', err);
      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setIsNetworkError(true);
        setGoogleError('Network error: Please check your internet connection');
      } else {
        setIsNetworkError(false);
        setGoogleError(err.response?.data?.error || 'Google login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    setGoogleError(error || 'Google sign-in failed');
  };

  const handleResendVerification = async () => {
    if (!emailVerificationData?.email) return;
    
    try {
      setLoading(true);
      const result = await resendVerificationEmail(emailVerificationData.email);
      
      if (result.success) {
        // Navigate to verification pending page
        navigate('/verify-email-pending', {
          state: {
            email: emailVerificationData.email,
            emailSent: result.emailSent,
            fromLogin: true
          }
        });
      } else {
        setError(result.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    console.log('🔍 LandingPage handleSubmit called');
    e.preventDefault();
    
    if (!isOnline) {
      // setError('No internet connection. Please check your network and try again.');
      // return;
    }
    
    try {
      setError('');
      setGoogleError('');
      setLoading(true);
      setIsNetworkError(false);
      
      // Save identifier if "Remember me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedIdentifier', formData.identifier);
      } else {
        localStorage.removeItem('rememberedIdentifier');
      }
      
      // Prepare login data
      const loginData = {
        identifier: formData.identifier,
        password: formData.password
      };
      
      // If "Keep signed in" is checked, add the expiration time
      if (keepSignedIn) {
        const midnight = new Date();
        midnight.setHours(24, 0, 0, 0);
        loginData.expires_at = midnight.getTime();
      }
      
      console.log('🔍 LandingPage attempting login with data:', { ...loginData, password: '***' });
      
      // Attempt login
      const loginResponse = await login(loginData.identifier, loginData.password, loginData.expires_at);
      
      console.log('🔍 LandingPage login response:', loginResponse);
      
      // Check if 2FA is required
      if (loginResponse && loginResponse.requires_2fa) {
        console.log('🔍 LandingPage 2FA required, showing 2FA screen');
        console.log('🔍 LandingPage requires2FA state before:', requires2FA);
        // Persist across unmounts (requires2FA in localStorage, since it's
        // not sensitive; the credentials themselves in-memory only - see
        // pendingLoginCredentials.js)
        localStorage.setItem('requires2FA', 'true');
        setPendingLoginCredentials(formData.identifier, formData.password);

        setRequires2FA(true);
        setLoading(false);
        return;
      }
      
      // Check if email verification is required
      if (loginResponse && loginResponse.email_verification_required) {
        setEmailVerificationRequired(true);
        setEmailVerificationData({
          email: formData.identifier,
          canResendVerification: loginResponse.can_resend_verification || false,
          emailResent: false
        });
        setLoading(false);
        return;
      }
      
      // Handle timeout settings
      if (keepSignedIn) {
        const minutesUntilMidnight = getMinutesUntilMidnight();
        updateInactivityTimeout(minutesUntilMidnight);
        localStorage.setItem('keepSignedIn', 'true');
        localStorage.setItem('signInExpiryTime', loginData.expires_at.toString());
      } else {
        updateInactivityTimeout(30);
        localStorage.removeItem('keepSignedIn');
        localStorage.removeItem('signInExpiryTime');
      }
      
      // Immediately navigate to dashboard after successful login
      navigate('/dashboard');
      
    } catch (err) {
      console.log('🔍 LandingPage handleSubmit error:', err);
      console.log('🔍 LandingPage handleSubmit err.response:', err.response);
      console.log('🔍 LandingPage handleSubmit err.response?.data:', err.response?.data);
      
      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setIsNetworkError(true);
        setError('Network error: Please check your internet connection');
      } else {
        setIsNetworkError(false);
        const errorMessage = err.response?.data?.error || err.error || err.message || 'Login failed';
        console.log('🔍 LandingPage handleSubmit errorMessage:', errorMessage);
        setError(errorMessage);
        console.log('🔍 LandingPage error state set to:', errorMessage);
        
        // Handle specific error cases
        if (errorMessage.includes('email verification')) {
          setEmailVerificationRequired(true);
          setEmailVerificationData({
            email: formData.identifier,
            canResendVerification: err.response?.data?.can_resend_verification || false,
            emailResent: false
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <ReceiptIcon color="primary" />,
      title: "AI-Powered Receipt Scanning",
      description: "Simply take a photo of your receipt and our AI automatically extracts and categorizes all transaction details"
    },
    {
      icon: <AccountBalanceIcon color="primary" />,
      title: "Multi-Account Management",
      description: "Manage multiple bank accounts, credit cards, and cash in one unified dashboard"
    },
    {
      icon: <TrendingUpIcon color="primary" />,
      title: "Real-time Analytics",
      description: "Get instant insights into your spending patterns with beautiful charts and reports"
    },
    {
      icon: <SavingsIcon color="primary" />,
      title: "Smart Goal Setting",
      description: "Set financial goals and track your progress with AI-powered recommendations"
    },
    {
      icon: <CategoryIcon color="primary" />,
      title: "Intelligent Categorization",
      description: "AI automatically categorizes transactions and learns from your preferences"
    },
    {
      icon: <AnalyticsIcon color="primary" />,
      title: "Predictive Budgeting",
      description: "AI-powered budget planning that adapts to your spending habits and goals"
    },
    {
      icon: <SecurityIcon color="primary" />,
      title: "Bank-Level Security",
      description: "Two-factor authentication and encryption keep your financial data completely secure"
    },
    {
      icon: <CloudSyncIcon color="primary" />,
      title: "Seamless Cloud Sync",
      description: "Access your data anywhere with automatic cloud synchronization across all devices"
    },
    {
      icon: <MobileFriendlyIcon color="primary" />,
      title: "Mobile-First Design",
      description: "Optimized for mobile with responsive design that works perfectly on any device"
    },
    {
      icon: <NotificationsIcon color="primary" />,
      title: "Smart Notifications",
      description: "Get intelligent alerts for unusual spending, budget limits, and personalized insights"
    }
  ];

  // Show Google popup after a short delay when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGooglePopup(true);
    }, 2000); // Show popup after 2 seconds

    return () => clearTimeout(timer);
  }, []);

  // Hide popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showGooglePopup) {
        setShowGooglePopup(false);
      }
    };

    if (showGooglePopup) {
      // Add a small delay to prevent immediate closing when popup appears
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showGooglePopup]);

  const handleGooglePopupClose = () => {
    setShowGooglePopup(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Google Sign-In Popup */}
      {showGooglePopup && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1300,
            bgcolor: 'transparent',
            p: 0,
            minWidth: 280,
            maxWidth: 320,
            transform: 'scale(0.75)',
            transformOrigin: 'top right',
            animation: 'slideInFromRight 0.5s ease-out',
            '@keyframes slideInFromRight': {
              '0%': {
                transform: 'translateX(100%) scale(0.75)',
                opacity: 0
              },
              '100%': {
                transform: 'translateX(0) scale(0.75)',
                opacity: 1
              }
            }
          }}
        >
          <GoogleSignInButton 
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={loading}
          />
        </Box>
      )}

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Login Section */}
          <Grid item xs={12} md={5} lg={4}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  color: 'white'
                }}
              >
                {/* Logo and title in login form */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  mb: 3,
                  gap: 2
                }}>
                  <img 
                    src="/logo.svg" 
                    alt="Expense Tracker Logo" 
                    style={{ 
                      width: '60px', 
                      height: '60px',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}
                  />
                  <Typography variant="h4" component="h2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Expense Tracker
                  </Typography>
                </Box>
                
                {!isOnline && (
                  <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WifiOffIcon />
                      <Typography variant="body2">
                        No internet connection. Please check your network.
                      </Typography>
                    </Box>
                  </Alert>
                )}
                
                {error && error !== 'trial_expired' && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
                    onClose={() => setError('')}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {error}
                    </Typography>
                    {(error.includes('Incorrect password') || error.includes('No account found')) && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                        {error.includes('Incorrect password') && '💡 Tip: Check if Caps Lock is on, or try resetting your password.'}
                        {error.includes('No account found') && '💡 Tip: Try using your email address instead of username, or sign up for a new account.'}
                      </Typography>
                    )}
                  </Alert>
                )}
                
                {googleError && !googleError.includes('development environment') && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 2, bgcolor: 'rgba(255,193,7,0.1)' }}
                    onClose={() => setGoogleError('')}
                  >
                    <Typography variant="body2">
                      {googleError}
                    </Typography>
                  </Alert>
                )}
                
                {emailVerificationRequired && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
                  >
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                      📧 Email Verification Required
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Please check your email and click the verification link to activate your account.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {emailVerificationData?.canResendVerification && !emailVerificationData?.emailResent && (
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={handleResendVerification}
                          disabled={loading}
                          sx={{ minWidth: '140px' }}
                        >
                          {loading ? <CircularProgress size={20} /> : 'Resend Email'}
                        </Button>
                      )}
                      <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => navigate('/verify-email-pending', {
                          state: {
                            email: emailVerificationData?.email,
                            emailSent: emailVerificationData?.emailResent || true,
                            fromLogin: true
                          }
                        })}
                        sx={{ minWidth: '140px' }}
                      >
                        Need Help?
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => setError('')}
                        sx={{ minWidth: '100px' }}
                      >
                        Try Again
                      </Button>
                    </Box>
                  </Alert>
                )}
                
                {!requires2FA ? (
                  <Fade in={!requires2FA}>
                    <Box component="form" onSubmit={handleSubmit}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="identifier"
                        label="Username or Email"
                        name="identifier"
                        autoComplete="username email"
                        value={formData.identifier}
                        onChange={handleInputChange}
                        autoFocus={!formData.identifier}
                        disabled={loading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.9)',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,1)',
                            },
                            '& .MuiInputBase-input': {
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            '&.Mui-focused': {
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            },
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -20px) scale(0.75)',
                          },
                        }}
                      />
                      <PasswordInput
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        id="password"
                        autoComplete="current-password"
                        value={formData.password}
                        onChange={handleInputChange}
                        disabled={loading}
                        showStrengthIndicator={false}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.9)',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,1)',
                            },
                            '& .MuiInputBase-input': {
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            '&.Mui-focused': {
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            },
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -20px) scale(0.75)',
                          },
                        }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={rememberMe} 
                            onChange={handleRememberMeChange}
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Remember my username/email"
                        sx={{ mt: 1, color: 'white' }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={keepSignedIn} 
                            onChange={handleKeepSignedInChange}
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Keep me signed in until midnight (remember this preference)"
                        sx={{ mt: 1, color: 'white' }}
                      />
                      {keepSignedIn && (
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          mt: 0.5, 
                          color: 'rgba(255,255,255,0.8)',
                          fontStyle: 'italic'
                        }}>
                          ✓ Your session will stay active until midnight today (preference will persist)
                        </Typography>
                      )}
                      
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        onClick={() => console.log('Button clicked!')}
                        sx={{ 
                          mt: 3, 
                          mb: 2,
                          bgcolor: 'white',
                          color: '#1976d2',
                          '&:hover': {
                            bgcolor: 'grey.100',
                          }
                        }}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Log In'}
                      </Button>
                      
                      <Divider sx={{ my: 2 }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          or
                        </Typography>
                      </Divider>
                      
                      <GoogleSignInButton 
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        disabled={loading}
                      />
                      
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                          <Link to="/forgot-password" style={{ color: 'white', textDecoration: 'underline' }}>
                            Forgot password?
                          </Link>
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                          <Link to="/login" style={{ color: 'white', textDecoration: 'underline' }}>
                            Simple login page
                          </Link>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Don't have an account?{' '}
                          <Link to="/register" style={{ color: 'white', textDecoration: 'underline' }}>
                            Sign Up
                          </Link>
                        </Typography>
                      </Box>
                    </Box>
                  </Fade>
                ) : (
                  <Slide direction="left" in={requires2FA}>
                    <Box>
                      <TwoFactorInput
                        onSubmit={handle2FASubmit}
                        loading={loading}
                        error={twoFactorError}
                        autoFocus={true}
                      />
                      
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button 
                          variant="text" 
                          onClick={handleBackToLogin}
                          disabled={loading}
                          sx={{ color: 'white' }}
                        >
                          ← Back to Login
                        </Button>
                      </Box>
                    </Box>
                  </Slide>
                )}
              </Paper>
            </Box>
          </Grid>

          {/* App Information Section */}
          <Grid item xs={12} md={7} lg={8}>
            <Box sx={{ pl: { md: 4 } }}>
              {/* Hero Section */}
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2.5rem', md: '3.5rem' }
                  }}
                >
                  Take Control of Your Finances
                </Typography>
                <Typography 
                  variant="h5" 
                  color="text.primary" 
                  sx={{ 
                    mb: 3,
                    fontSize: { xs: '1.2rem', md: '1.5rem' }
                  }}
                >
                  The smart expense tracker that helps you understand your spending, 
                  set financial goals, and build better money habits, putting ExTra money in your pocket.
                </Typography>

              </Box>

              {/* Features Grid */}
              <Box sx={{ mb: 6 }}>
                <Grid container spacing={3}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          height: '100%',
                          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(66, 165, 245, 0.15) 100%)',
                          border: '1px solid',
                          borderColor: 'rgba(25, 118, 210, 0.2)',
                          borderRadius: 3,
                          transition: 'all 0.3s ease-in-out',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(25, 118, 210, 0.2)',
                            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.25) 0%, rgba(66, 165, 245, 0.25) 100%)',
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box sx={{ 
                              p: 1.5, 
                              borderRadius: 2, 
                              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                              color: 'white',
                              mr: 2,
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                              transition: 'all 0.3s ease-in-out'
                            }}>
                              {feature.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                  mb: 0.5, 
                                  fontWeight: 700, 
                                  fontSize: '1.1rem',
                                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  letterSpacing: '-0.02em'
                                }}
                              >
                                {feature.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.primary" 
                                sx={{ 
                                  lineHeight: 1.6,
                                  fontSize: '0.9rem',
                                  fontWeight: 400,
                                  opacity: 0.9,
                                  letterSpacing: '0.01em'
                                }}
                              >
                                {feature.description}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>


            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingPage; 